import {
  action,
  atom,
  onConnect,
  withAssign,
  withInit,
  withOnUpdate,
} from '@reatom/framework'
import { withLocalStorage } from '@reatom/persist-web-storage'

import { setMakerFilterEvent } from '@root/features/trade/tables/trades-table/atoms'
import { STORAGE_PREFIX } from '@root/shared'

import { next, TradesTableViewOption } from '../model/trades-table-view-option'

import { tradeMakerFilterAtom } from './reatom-trades-table'

import type { Ctx } from '@reatom/framework'

const QUERY_PARAM = 'tr_tab'

const syncTabWithViewOption = (ctx: Ctx, option: TradesTableViewOption) => {
  if (
    option === TradesTableViewOption.Chart &&
    ctx.get(tradeTableTabAtom) === 'trades'
  ) {
    tradeTableTabAtom(ctx, 'holders')
  }
}

export type TradeTableTab =
  | 'trades'
  | 'orders'
  | 'positions'
  | 'history'
  | 'holders'
  | 'top-holders'
  | 'dev-tokens'

const TABS = new Set<TradeTableTab>([
  'trades',
  'orders',
  'positions',
  'history',
  'holders',
  'top-holders',
])

export const tradeTableTabAtom = atom<TradeTableTab>(
  'trades',
  'tradeTableTabAtom',
).pipe(
  withInit(() => {
    const url = new URL(window.location.href)
    const tab = url.searchParams.get(QUERY_PARAM)

    if (tab && TABS.has(tab as TradeTableTab)) {
      return tab as TradeTableTab
    }

    return 'trades'
  }),
  withOnUpdate((ctx, tab) => {
    if (
      tab === 'trades' &&
      ctx.get(tradesTableViewOptionAtom) === TradesTableViewOption.Chart
    ) {
      tradeTableTabAtom(ctx, 'holders')
      return
    }

    const url = new URL(window.location.href)
    url.searchParams.set(QUERY_PARAM, tab)
    window.history.replaceState(history.state, '', url)
  }),
)

onConnect(tradeTableTabAtom, () => {
  const unsubscribeMakerFilter = setMakerFilterEvent.onCall(
    (ctx, { maker }) => {
      if (ctx.get(tradesTableViewOptionAtom) === TradesTableViewOption.Chart) {
        tradeMakerFilterAtom(ctx, maker)
        return
      }

      const tab = ctx.get(tradeTableTabAtom)
      if (tab !== 'trades') {
        tradeMakerFilterAtom(ctx, maker)
        tradeTableTabAtom(ctx, 'trades')
      }
    },
  )

  return () => {
    unsubscribeMakerFilter()
  }
})

export const tradesTableViewOptionAtom = atom<TradesTableViewOption>(
  TradesTableViewOption.Table,
  'tradesTableViewOptionAtom',
).pipe(
  withLocalStorage(`${STORAGE_PREFIX}/v1/TradesTableViewOption`),
  withOnUpdate((ctx, option) => {
    syncTabWithViewOption(ctx, option)
  }),
  withAssign(() => ({
    toggle: action((ctx) => {
      tradesTableViewOptionAtom(ctx, next[ctx.get(tradesTableViewOptionAtom)])
    }, 'tradesTableViewOptionAtom.toggle'),
  })),
)

onConnect(tradesTableViewOptionAtom, (ctx) => {
  syncTabWithViewOption(ctx, ctx.get(tradesTableViewOptionAtom))
})
